import {
  Box,
  Button,
  Divider,
  Grid,
  Paper,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import ScrollContainer from "react-indiana-drag-scroll";
import { useEffect, useState } from "react";

import colors from "../../../styles/colors/colors";
import fontWeight from "../../../styles/mui/fontWeight";

import {
  Action,
  AlignCenter,
  ApprovedVisitContainer,
  ApprovedVisitValue,
  DisciplineContainerStyle,
  DisciplineTableCell,
  displayFlex,
  DisplayNone,
  FaChevronCircleDownIcon,
  FaChevronCircleUpIcon,
  getColorValue,
  GridBody,
  GridTableContent,
  HeaderTableCell,
  HeaderTitle,
  HorizontalDivider,
  MainConatiner,
  PatientTableCell,
  ReviewArrowIcon,
  ReviewArrowIconDown,
  ReviewButton,
  TableCellSticky,
  TableCellStickyHeader,
  TableHeight,
  TableMain,
  TableMainHead,
  TableMainHeadRow,
  VerticalDivider,
  ViewBoxStyle,
  VisitTableCell,
} from "../Style/DisciplineStyle";
import { NO_RECORD_FOUND } from "../../../constants/tostmsg";
import { DateFormats } from "../../../constants/Date";
import { flexAlignCentre } from "../../../styles/mui/styles/display";
import { decryptId, length } from "../../../utils";
import {
  DefaultPaginationData,
  DisciplineData,
  DisciplineHeaderLabel,
  disciplineList,
  DisciplineRecord,
} from "../../../constants/Disciplines";
import {
  Discipline,
  DisciplinePayload,
  PaginationPayload,
  PatientList,
} from "../../../models/Authorization";
import { formatDate } from "../../../utils/DateTime";
import CustomTablePagination from "../../../App/components/CustomTablePagination";
import { DisciplineResponse } from "../../../models/Api/Authorization";
import { useParams } from "react-router-dom";
import DisciplineDetails from "../Container/DisciplineDetails";
import { DEFAULT_PAGE, PER_PAGE } from "../../../constants/FunctionalUtils";

export interface PropsFromState {
  disciplineListData: DisciplineResponse;
}
export interface PropsFromDispatch {
  getDisciplineList: (payload: DisciplinePayload) => void;
}
type Props = PropsFromState & PropsFromDispatch;

const Disciplines: React.FC<Props> = ({
  disciplineListData,
  getDisciplineList,
}: Props) => {
  const [showDiscipline, setShowDiscipline] = useState(true);
  const [serviceDetails, setServiceDetails] = useState(false);
  const [disciplineRecord, setDisciplineRecord] =
    useState<Discipline>(DisciplineRecord);
  const [disciplineRecords, setdisciplineRecords] = useState<Discipline[]>([]);
  const [referralId, setReferralId] = useState<number | null>(null);
  const [paginationData, setPaginationData] = useState<PaginationPayload>(
    DefaultPaginationData
  );
  const { response } = disciplineListData;
  const { authNo } = useParams();
  const DisciplineToggleIcon = !showDiscipline ? (
    <FaChevronCircleDownIcon
      onClick={() => setShowDiscipline(true)}
      data-testid="hide-discipline"
    />
  ) : (
    <FaChevronCircleUpIcon
      onClick={() => setShowDiscipline(false)}
      data-testid="show-discipline"
    />
  );

  useEffect(() => {
    if (response && response.disciplineList) {
      setdisciplineRecords(response.disciplineList);
    }
  }, [response]);

  useEffect(() => {
    const loadReferralId = async () => {
      if (!authNo) {
        setReferralId(null);
        return;
      }

      const decryptedId = await decryptId(authNo);
      const parsed = Number(decryptedId);
      setReferralId(Number.isFinite(parsed) ? parsed : null);
    };

    void loadReferralId().catch(() => setReferralId(null));
  }, [authNo]);

  useEffect(() => {
    if (referralId !== null) {
      const payload: DisciplinePayload = {
        referralId,
        pageNo: DEFAULT_PAGE,
        pageSize: PER_PAGE,
        skipPagination: false,
      };
      getDisciplineList(payload);
    }
  }, [referralId]);

  const handleReview = (row: Discipline) => {
    setDisciplineRecord(row);
    setServiceDetails(true);
  };

  const validation = () => {
    return (
      <TableRow>
        <TableCell colSpan={14}>
          <Box display="flex" justifyContent={"center"}>
            <Typography
              variant="subtitle2"
              fontWeight={fontWeight.Weight[4]}
              sx={DisciplineTableCell}
            >
              {NO_RECORD_FOUND}
            </Typography>
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  const handlePagination = (paginationData: PaginationPayload) => {
    if (referralId === null) {
      return;
    }

    const payload: DisciplinePayload = {
      ...paginationData,
      referralId,
      skipPagination: false,
    };
    setPaginationData(paginationData);
    getDisciplineList(payload);
  };
  const isVisitCellIndex = (index: number) =>
    index === 6 || index === 7 || index === 8;

  const renderPrimaryCell = (data: PatientList, index: number, row: any) => {
    if (index === 0) {
      return (
        <TableCell key={data.value} sx={PatientTableCell}>
          <Typography variant="subtitle2" fontWeight={data.fontWeight}>
            {formatDate(
              row[data.value],
              DateFormats.MM_DD_YYYY_comma_hh_mm_ss_a
            )}
          </Typography>
        </TableCell>
      );
    }
    return null;
  };

  const renderDefaultCellValue = (
    data: PatientList,
    row: any,
    index: number
  ) => {
    if (index === 4 || index === 5) {
      return formatDate(row[data.value], DateFormats.MM_DD_YYYY);
    }
    return row[data.value];
  };

  const renderDisciplineCell = (
    data: PatientList,
    index: number,
    row: any,
    disciplineList: PatientList[]
  ) => {
    const primary = renderPrimaryCell(data, index, row);
    if (primary) return primary;

    return (
      <TableCell
        key={data.value}
        sx={isVisitCellIndex(index) ? VisitTableCell : PatientTableCell}
      >
        <Typography
          variant="subtitle2"
          fontWeight={data.fontWeight}
          color={getColorValue(data, row, disciplineList)}
        >
          {renderDefaultCellValue(data, row, index)}
        </Typography>
      </TableCell>
    );
  };

  const renderTableCell = (tableCell: { label: DisciplineData; style: { cell: { minWidth: string; width: string; }; }; }, index: number) => {
    if (index === DisciplineHeaderLabel.length - 1) {
      return (
        <TableCellStickyHeader key={tableCell.label}>
          <Box sx={tableCell.style?.cell}>
            <Box sx={AlignCenter}>
              <Typography
                variant="body2"
                fontWeight={fontWeight.Weight[3]}
                color={colors.black[16]}
                sx={Action}
              >
                {tableCell.label}
              </Typography>
            </Box>
          </Box>
        </TableCellStickyHeader>
      );
    }

    return (
      <TableCell key={tableCell.label} sx={HeaderTableCell}>
        <Box sx={tableCell.style?.cell}>
          <Box sx={AlignCenter}>
            <Typography
              variant="body2"
              fontWeight={fontWeight.Weight[3]}
              color={colors.black[16]}
            >
              {tableCell.label}
            </Typography>
          </Box>
        </Box>
      </TableCell>
    );
  };
  return (
    <>
      <Grid container sx={GridBody}>
        <Grid size={12}>
          <Grid container sx={MainConatiner}>
            <Grid size={6} sx={displayFlex}>
              {DisciplineToggleIcon}
              <Typography
                variant="subtitle1"
                color={colors.fonts[20]}
                fontWeight={fontWeight.Weight[5]}
                sx={HeaderTitle}
              >
                DISCIPLINES
              </Typography>
              <Divider orientation="vertical" flexItem sx={VerticalDivider} />
              <Typography
                variant="subtitle2"
                fontWeight={fontWeight.Weight[2]}
                color={colors.black[2]}
                pt={"0.3rem"}
              >
                Below is the summary for the all discipline requests.
              </Typography>
            </Grid>
            <Grid size={6} sx={ApprovedVisitContainer}>
              <Typography
                variant="subtitle1"
                color={colors.fonts[20]}
                fontWeight={fontWeight.Weight[4]}
                sx={HeaderTitle}
              >
                Total Visits Approved:
              </Typography>
              <Typography
                variant="subtitle1"
                fontWeight={fontWeight.Weight[6]}
                color={colors.black[15]}
                sx={ApprovedVisitValue}
              >
                {response && response.totalVisitsApproved}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Divider sx={HorizontalDivider} />
        <Grid
          size={12}
          sx={[GridTableContent, showDiscipline ? {} : { display: "none" }]}
        >
          <TableContainer sx={DisciplineContainerStyle} component={Paper}>
            <ScrollContainer
              className="scroll-container"
              hideScrollbars={false}
            >
              <TableMain>
                <TableMainHead>
                  <TableMainHeadRow>
                    {DisciplineHeaderLabel &&
                      DisciplineHeaderLabel.map((cell, index) =>
                        renderTableCell(cell, index)
                      )}
                  </TableMainHeadRow>
                </TableMainHead>
                <TableBody>
                  {(length(disciplineRecords) as unknown as number)
                    ? disciplineRecords.map((row: Discipline, rowIndex: number) => {
                        return (
                          <TableRow key={rowIndex} sx={TableHeight}>
                            {disciplineList.map(
                              (data: PatientList, colIndex: number) =>
                                renderDisciplineCell(
                                  data,
                                  colIndex,
                                  row,
                                  disciplineList
                                )
                            )}
                            <TableCellSticky sx={PatientTableCell}>
                              <Box sx={ViewBoxStyle}>
                                <>
                                  <Grid sx={displayFlex}>
                                    {(() => {
                                      const isCurrentRow =
                                        row.serviceRequestDisciplineId ===
                                        disciplineRecord.serviceRequestDisciplineId;
                                      return (
                                        <Button
                                          variant="contained"
                                          onMouseDown={() => handleReview(row)}
                                          sx={ReviewButton}
                                          disabled={isCurrentRow}
                                          data-testid={`review-discipline-${rowIndex}`}
                                        >
                                          <Box sx={flexAlignCentre}>
                                            <Typography
                                              variant="subtitle2"
                                              fontWeight={fontWeight.Weight[4]}
                                              color={colors.white[1]}
                                            >
                                              REVIEW
                                            </Typography>
                                            {isCurrentRow ? (
                                              <ReviewArrowIconDown />
                                            ) : (
                                              <ReviewArrowIcon />
                                            )}
                                          </Box>
                                        </Button>
                                      );
                                    })()}
                                  </Grid>
                                </>
                              </Box>
                            </TableCellSticky>
                          </TableRow>
                        );
                      })
                    : validation()}
                </TableBody>
              </TableMain>
            </ScrollContainer>
          </TableContainer>
        </Grid>
        <Grid size={12}>
          <CustomTablePagination
            totalRecords={
              response && response?.totalRecords ? response?.totalRecords : 0
            }
            handlePagination={handlePagination}
            paginationData={paginationData}
            paginationInjectedTo="discipline-list"
          />
        </Grid>

        <Grid size={12} sx={showDiscipline ? {} : DisplayNone}>
          {serviceDetails && (
            <DisciplineDetails
              setServiceDetails={setServiceDetails}
              setDisciplineRecord={setDisciplineRecord}
              disciplineRecord={disciplineRecord}
            />
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default Disciplines;
